import { bookActions, loadingActions } from '@/actions';
import { BookAppointmentContext } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import { SCREEN_NAME } from '@/constants/analytics';
import { ROUTE_BUILDER } from '@/constants/pages';
import { getBookingStartingPoint, setBookingStartingPoint } from '@/helpers';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { fetchPlace } from '@/helpers/placeHelperTS';
import { Place } from '@/types/state/shared';
import { User } from '@/types/user';
import { MutableRefObject } from 'react';
import { DealDetailItem } from '../../types/dealDetails';
import { getServiceDetails, showErrorToast } from './DealDetails.helpers';

export function handlePageLeave() {
  /**
   * @TODO - investigate if we need to cancel some tracking flow
   */
}

export function handleBackClick(
  history: { goBack: () => void; push: (path: string) => void },
  searchedKeyword?: string,
  searchedLocation?: string,
) {
  handlePageLeave();

  if (window.history.length > 1) {
    /**
     * @TODO - check caching solution for serp results
     */
    history.goBack();
  } else {
    history.push(ROUTE_BUILDER.dealsSerpPreview(searchedKeyword, searchedLocation));
  }
}

/**
 * Fetches place details with loading indicator. Guarantees loading is hidden on all paths.
 */
async function fetchDealPlace(
  place: { id: number; slug: string },
  dealId: number,
  appDispatch: (action: any) => void,
  abortSignal?: AbortSignal,
): Promise<Place | null> {
  appDispatch(loadingActions.show('fetchPlaceDetails'));

  try {
    const placeDetails = await fetchPlace({ id: place.id, slug: place.slug, dealId, abortSignal });

    if (!placeDetails) {
      showErrorToast();
      return null;
    }

    return placeDetails;
  } finally {
    appDispatch(loadingActions.hide());
  }
}

export async function handleBookClick(
  data: DealDetailItem,
  user: User,
  appDispatch: (action: any) => void,
  abortControllerRef: MutableRefObject<AbortController | null>,
  context: BookAppointmentContext,
  history: any /** react-router history */,
) {
  const id = data?.place?.id;
  const slug = data?.place?.slug;

  if (!id || !slug) {
    showErrorToast();
    return null;
  }

  const placeDetails = await fetchDealPlace({ id, slug }, data.id, appDispatch, abortControllerRef.current?.signal);

  if (!placeDetails) return;

  const services = data.services;
  const service = services?.[0];

  if (!service?.id) {
    showErrorToast();
    return;
  }

  const serviceDetails = getServiceDetails(placeDetails, service.id);

  if (!serviceDetails) {
    console.log('Failed to find service details for service id', { serviceId: service.id });
    showErrorToast();
    return false;
  }

  appDispatch(bookActions.initBookAppointment(serviceDetails, placeDetails));

  let bookingStartingPoint = getBookingStartingPoint();

  if (!bookingStartingPoint) {
    bookingStartingPoint = SCREEN_NAME.DEAL_DETAILS;
    setBookingStartingPoint(bookingStartingPoint);
  }

  bookingFlowTrackingSession.initiate(placeDetails, user, bookingStartingPoint);

  const shouldPickAddOn = serviceDetails?.extra?.addOnServices?.length;

  if (shouldPickAddOn) {
    // reset view when re-opened
    context.updateView(undefined);
    context.open();
    return;
  }

  const serviceSlug = serviceDetails.about.slug ? serviceDetails.about.slug + '-' : '';
  history.push({ pathname: `/boka-tjanst/${slug}${id}/${serviceSlug}${service.id}`, search: `dealId=${data.id}` });
}
